import type { TFunction } from 'i18next'
import type { AppVersion, MainPackageInfo, Platform } from '@/types'
import { isMockSpaceId } from '@/lib/currentOrg'
import {
  compareVersionDesc,
  isPlatformPackageUploaded,
  isPlatformPackageUsable,
  platformLabel,
} from '../platform'
import type { VersionForm } from './formModel'
import { isOptionalHttpsUrl } from './httpsUrl'
/** 提交审核校验错误码（对应 versionForm.err* i18n） */
export type VersionSubmitErrorCode =
  | 'errMainPackage'
  | 'errVersionCode'
  | 'errNoPlatformPackage'
  | 'errFirstPlatformPackages'
  | 'errMissingPlatformPackage'
  | 'errUploading'
  | 'errChangelog'
  | 'errListingCategory'
  | 'errOwnerSpace'
  | 'errDescription'
  | 'errUrl'
  | 'errScheduleTime'
  | 'errReviewing'
  | 'errScheduled'

export type VersionSubmitError = {
  code: VersionSubmitErrorCode
  /** 仅 errVersionCode 使用 */
  max?: string
  /** errFirstPlatformPackages / errMissingPlatformPackage：仍缺的声明平台 */
  platforms?: Platform[]
}

export type ValidateVersionSubmitInput = {
  version: AppVersion | null | undefined
  form: VersionForm | null | undefined
  /** 版本号下限：服务端 max_online_version，缺失时见 maxEverReleasedVersion */
  maxReleased?: string
  hasUploading: boolean
  /** 同应用其他版本，用于互斥状态校验 */
  siblingVersions: AppVersion[]
  /** 可注入「现在」，便于定时上线单测 */
  nowMs?: number
}

/**
 * 「去配置」用的版本快照：store / draft / mainParse 互补。
 * assumeFirstVersion：createApp 等场景下 is_first_version 缺失时按首版校验。
 */
export function resolveVersionForPlatformCheck(opts: {
  version?: AppVersion | null
  draft?: AppVersion | null
  mainParse?: MainPackageInfo | null
  assumeFirstVersion?: boolean
}): AppVersion | undefined {
  const { version, draft, mainParse, assumeFirstVersion } = opts
  const base = version ?? draft ?? undefined
  if (!base) return undefined

  const platforms =
    firstNonEmpty(
      version?.platforms,
      draft?.platforms,
      mainParse?.platforms,
      base.platforms,
    ) ?? []

  const platform_packages =
    firstNonEmpty(version?.platform_packages, draft?.platform_packages) ??
    base.platform_packages

  return {
    ...base,
    platforms,
    main_package:
      version?.main_package ??
      draft?.main_package ??
      mainParse ??
      base.main_package,
    platform_packages,
    is_first_version:
      version?.is_first_version ??
      draft?.is_first_version ??
      assumeFirstVersion,
  }
}

function firstNonEmpty<T>(
  ...candidates: Array<T[] | undefined | null>
): T[] | undefined {
  for (const c of candidates) {
    if (c && c.length > 0) return c
  }
  return undefined
}

/** 主包 / 子包槽位校验；「去配置」与提审共用 */
export function validatePlatformPackages(
  version: AppVersion | null | undefined,
): VersionSubmitError | null {
  if (!version?.main_package) return { code: 'errMainPackage' }
  if (!version.main_package.needs_platform_packages) return null

  const pkgs = version.platform_packages ?? []
  const declared = version.platforms

  // 首版无历史包：声明几个就必须新传几个
  if (version.is_first_version) {
    const missing = declared.filter(
      (p) =>
        !pkgs.some((pkg) => pkg.platform === p && isPlatformPackageUploaded(pkg)),
    )
    if (missing.length) {
      return { code: 'errFirstPlatformPackages', platforms: missing }
    }
    return null
  }

  // 后续版：至少 1 个本次新传；其余须有服务端确认的历史包
  if (!pkgs.some(isPlatformPackageUploaded)) {
    return { code: 'errNoPlatformPackage' }
  }
  const missing = declared.filter(
    (p) => !pkgs.some((pkg) => pkg.platform === p && isPlatformPackageUsable(pkg)),
  )
  if (missing.length) {
    return { code: 'errMissingPlatformPackage', platforms: missing }
  }
  return null
}

/** 把平台校验错误格式化成可展示文案（platforms 用展示名） */
export function formatPlatformPackagesError(
  t: TFunction,
  err: VersionSubmitError,
): string {
  return t(`versionForm.${err.code}`, {
    max: err.max,
    platforms: err.platforms?.map(platformLabel).join(' / '),
  })
}

/**
 * 版本提交审核必填项校验（命中即停）。
 * 对齐 C4：主包 → 版本号 → 平台包 → 上传中 → 更新日志(非首版) →
 * 上架空间类别 → 英语详细描述 → 展示 URL → 定时时间 → 审核中/待上线互斥。
 */
export function validateVersionSubmit(
  input: ValidateVersionSubmitInput,
): VersionSubmitError | null {
  const {
    version,
    form,
    maxReleased,
    hasUploading,
    siblingVersions,
    nowMs = Date.now(),
  } = input

  if (!version || !form) return { code: 'errMainPackage' }
  if (!version.main_package) return { code: 'errMainPackage' }

  if (maxReleased && compareVersionDesc(version.version, maxReleased) >= 0) {
    return { code: 'errVersionCode', max: maxReleased }
  }

  const platformErr = validatePlatformPackages(version)
  if (platformErr) return platformErr

  if (hasUploading) return { code: 'errUploading' }

  const anyChangelog = Object.values(form.changelog).some((v) => v.trim())
  if (!version.is_first_version && !anyChangelog) {
    return { code: 'errChangelog' }
  }

  if (
    form.app_fixed_info.distribution_type === 'all_spaces' &&
    form.app_fixed_info.listing_space_types.length === 0
  ) {
    return { code: 'errListingCategory' }
  }
  if (
    form.app_fixed_info.distribution_type === 'self_built' &&
    (!form.app_fixed_info.owner_space_id?.trim() ||
      isMockSpaceId(form.app_fixed_info.owner_space_id))
  ) {
    return { code: 'errOwnerSpace' }
  }
  const enDesc = form.display_info.localizations['en']?.description ?? ''
  if (!enDesc.trim()) return { code: 'errDescription' }

  const { website_url, privacy_policy_url, terms_url } = form.display_info
  if (
    !isOptionalHttpsUrl(website_url) ||
    !isOptionalHttpsUrl(privacy_policy_url) ||
    !isOptionalHttpsUrl(terms_url)
  ) {
    return { code: 'errUrl' }
  }
  if (!form.releaseImmediate) {
    const at = form.scheduled_at ? new Date(form.scheduled_at).getTime() : 0
    if (at <= nowMs + 3600_000) return { code: 'errScheduleTime' }
  }

  if (siblingVersions.some((v) => v.status === 'reviewing' && v.id !== version.id)) {
    return { code: 'errReviewing' }
  }
  if (siblingVersions.some((v) => v.status === 'scheduled' && v.id !== version.id)) {
    return { code: 'errScheduled' }
  }

  return null
}
